import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import {
  PageContainer,
  Container,
  Section,
  PageTitle,
  SectionTitle,
  LazyBackgroundImage,
  ContentText,
  SectionDivider,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
  ContactButton,
} from '../components/shared/SharedComponents';
import { useContent } from '../hooks/useContent';
import { useNavigationAwareAnimations } from '../hooks/useNavigationAwareAnimations';
import { preloadCriticalImages } from '../utils/imagePreloader';
import { PRELOAD_SERVICES_CRITICAL } from '../config/preloadConfig';

// Tier Section
const TierSection = styled(Section)`
  padding: 30px 0;
  position: relative;
`;

const TierHeader = styled.div`
  text-align: left;
  margin-bottom: 20px;
  position: relative;
`;

const TierTitle = styled(SectionTitle)`
  text-align: left;
  margin-bottom: 12px;

  span {
    background: linear-gradient(135deg, #BF1922, #8B1419);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-right: 8px;
  }
`;

const TierDescription = styled.p`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.8);
  text-align: left;
  line-height: 1.65;
`;

const CustomTierContent = styled.div`
  text-align: left;
`;

const ServicesSectionDivider = styled(SectionDivider)`
  margin: 32px auto 16px auto;
`;

const ServicesList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 500px), 1fr));
  gap: 12px 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px 0;
  }
`;

const ServiceListItem = styled(motion.li)`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 14px;
  align-items: start;
  padding: 6px 0;
  font-size: 15px;
  line-height: 1.6;
  color: #1d334e;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    margin-top: 8px;
    border-radius: 50%;
    background: #BF1922;
    box-shadow: 0 0 0 2px rgba(191, 25, 34, 0.2);
  }
`;

const ServiceItemText = styled.span`
  font-weight: 500;
  hyphens: auto;
  overflow-wrap: normal;
`;

const ServiceItemDescription = styled.span`
  font-weight: 400;
  color: rgba(29, 51, 78, 0.75);
`;

const TierImageSection = styled(motion.div)`
  margin: 60px 0;
  border-radius: 24px;
  overflow: hidden;
  height: 300px;
  background-size: cover;
  background-position: center;
  
  @media (max-width: 768px) {
    height: 200px;
    margin: 40px 0;
  }
`;


function Services() {
  useNavigationAwareAnimations();
  const { services, footerInfo, contactInfo, site } = useContent();

  useEffect(() => {
    if (PRELOAD_SERVICES_CRITICAL.length > 0) {
      preloadCriticalImages(PRELOAD_SERVICES_CRITICAL);
    }
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <>
      <SEO
        title={services?.title || 'Services - APEXGATE GLOBAL'}
        description={services?.description || 'Comprehensive business solutions and services'}
      />
      <StructuredData
        type="Service"
        data={{
          service: {
            name: services?.title || 'Services',
            description: services?.description || 'Comprehensive business solutions and services',
            provider: { name: 'APEXGATE GLOBAL', url: 'https://apexgateglobal.com' }
          },
          organization: { footerInfo, contactInfo },
          site
        }}
        includeOrganization={true}
      />
      <PageContainer>
        <Container>
          <article>
            {/* Header */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={heroStaggerContainerVariants}
            >
              <PageTitle
                className="with-underline"
                variants={heroStaggerItemVariants}
              >
                {services.title}
              </PageTitle>
            </motion.div>

            {/* Service Sections */}
            {services.sections.map((tier, tierIndex) => (
              <React.Fragment key={tierIndex}>
                <TierSection>
                  <TierHeader>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <TierTitle className="tier">
                        {tier.tier && <span>{tier.tier}</span>} {tier.title}
                      </TierTitle>
                      {tier.description && <TierDescription>{tier.description}</TierDescription>}
                    </motion.div>
                  </TierHeader>

                  {tier.paragraphs && Array.isArray(tier.paragraphs) && tier.paragraphs.length > 0 ? (
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <ContentText>
                        {tier.paragraphs.map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </ContentText>
                    </motion.div>
                  ) : tier.services && Array.isArray(tier.services) && tier.services.length > 0 ? (
                    <ServicesList
                      role="list"
                      variants={containerVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {tier.services.map((service, index) => (
                        <ServiceListItem key={index} variants={itemVariants}>
                          <ServiceItemText>
                            {service.title}
                            {service.description && (
                              <>
                                {' – '}
                                <ServiceItemDescription>{service.description}</ServiceItemDescription>
                              </>
                            )}
                          </ServiceItemText>
                        </ServiceListItem>
                      ))}
                    </ServicesList>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <CustomTierContent>
                        <ContactButton to="/contact">{services?.buttonText}</ContactButton>
                      </CustomTierContent>
                    </motion.div>
                  )}
                </TierSection>

                {/* Add visual separator images between tiers */}
                {tierIndex === 0 && (
                  <LazyBackgroundImage src="/ui/office.png">
                    <TierImageSection
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    />
                  </LazyBackgroundImage>
                )}
                {tierIndex === 1 && (
                  <LazyBackgroundImage src="/services-images/services-hero.png">
                    <TierImageSection
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    />
                  </LazyBackgroundImage>
                )}
                {tierIndex === 2 && (
                  <LazyBackgroundImage src="/services-images/strategic-partnership.png">
                    <TierImageSection
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    />
                  </LazyBackgroundImage>
                )}
                {(tierIndex < services.sections.length - 1 || String(tier?.tier || '').trim() === 'V.') && <ServicesSectionDivider />}
              </React.Fragment>
            ))}

            {/* CTA */}
            <Section style={{ textAlign: 'center', padding: '60px 0' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                viewport={{ once: true }}
              >
                <ContactButton to="/contact">{services?.buttonText}</ContactButton>
              </motion.div>
            </Section>

          </article>
        </Container>
      </PageContainer>
    </>
  );
}

export default Services;
